import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Flex,
} from '@kognitos/lattice';

export interface IAssetPublishDialogProps {
  open: boolean;
  onClose: () => void;
  /** e.g. "Publish 'Invoice intake'". */
  title: string;
  /** Under the title: what publishing does. */
  description: ReactNode;
  /** The version this publish saves as; omit while it isn't known. */
  nextVersion?: string;
  /** What the publish needs or affects, e.g. connections to set up. */
  children?: ReactNode;
  onPublish: () => void;
  isPublishing?: boolean;
  /** Publish stays disabled, e.g. while something it needs is missing. */
  publishDisabled?: boolean;
  'data-testid'?: string;
}

/**
 * The confirmation before an asset publishes a new version: what publishing
 * does, the version it saves as, anything it needs first, then Cancel and
 * Publish. Each builder words the description and fills the body.
 */
export function AssetPublishDialog({
  open,
  onClose,
  title,
  description,
  nextVersion,
  children,
  onPublish,
  isPublishing = false,
  publishDisabled = false,
  'data-testid': testId,
}: IAssetPublishDialogProps) {
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription asChild>
            <Flex direction="column" gap={1.5} width="full">
              {description}
            </Flex>
          </DialogDescription>
        </DialogHeader>

        {nextVersion && (
          <Alert variant="default">
            <AlertDescription>
              {t('common.assetWorkspace.publishVersion', {
                version: nextVersion,
              })}
            </AlertDescription>
          </Alert>
        )}

        {children}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPublishing}>
            {t('common.action.cancel')}
          </Button>
          <Button
            onClick={onPublish}
            disabled={publishDisabled || isPublishing}
            isLoading={isPublishing}
          >
            {isPublishing
              ? t('common.assetWorkspace.publishing')
              : t('common.assetWorkspace.publish')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
